<template>
  <div class="product-filters">
    <div v-if="loading || !filtersLoaded" class="product-filters-skeleton">
      <div class="row">
        <div class="col-12">
          <div class="skeleton skeleton--search-input"></div>
        </div>
      </div>
    </div>

    <div v-else-if="filtersLoaded">
      <div class="row gx-3">
        <div class="col-12 align-items-center d-flex gap-2">
          <!-- Filter button mobile -->
          <button
            :class="['btn btn--primary d-lg-none']"
            @click="filterModalOpen = true"
          >
            Filter
            <span v-if="getActiveFiltersCount > 0" class="badge badge--sm">
              {{ getActiveFiltersCount }}
            </span>
            <i class="icon icon-adjustments"></i>
          </button>

          <!-- Zoekveld -->
          <input
            ref="searchInput"
            v-model="localSearch"
            type="text"
            :placeholder="searchPlaceholder"
            class="form-control search-input m-0"
            @input="handleSearchInput"
            @keydown.enter="handleSearchEnter"
            aria-label="Zoekterm"
          />

          <!-- Modal on mobile -->
          <div class="configurator-modal px-0" v-if="filterModalOpen">
            <div
              class="configurator-modal__overlay"
              @click="filterModalOpen = false"
            ></div>
            <div class="container-xl">
              <div class="row mx-auto">
                <div
                  class="col-12 col-lg-10 col-xl-8 mx-auto configurator-modal__card"
                >
                  <!-- Title bar -->
                  <div class="d-flex gap-2 pb-3 align-items-center">
                    <span class="h2 lh-base text-wrap-balance flex-grow-1 mb-0"
                      >Filter</span
                    >
                    <button
                      @click="filterModalOpen = false"
                      :class="['btn btn--gray btn--icon-sm btn--rotate fs-xs']"
                    >
                      <i class="icon icon-close"></i>
                    </button>
                  </div>

                  <!-- Content -->
                  <div class="row configurator-modal__content gap-2 pb-2">
                    <FilterDropdown
                      v-for="filter in availableFilters"
                      :key="filter.key"
                      :filter="filter"
                      :is-open="isDropdownOpen(filter.key)"
                      :selected-values="getSelectedValues(filter.key)"
                      :options="filterOptions[filter.optionsKey]"
                      :loading="
                        filter.key === 'categories' && loadingSubcategories
                      "
                      @toggle="toggleDropdown"
                      @select="handleFilterSelect"
                      @select-all="handleSelectAll"
                    />

                    <!-- Sort dropdown mobile -->
                    <div class="filter-select col-12">
                      <button
                        class="form-select w-100"
                        :class="{ active: isDropdownOpen('sort-mobile') }"
                        type="button"
                        @click="toggleDropdown('sort-mobile')"
                      >
                        Sorteren
                      </button>
                      <div
                        v-if="isDropdownOpen('sort-mobile')"
                        class="filter-select__dropdown"
                      >
                        <div
                          v-for="option in sortOptions"
                          :key="option.value"
                          class="item"
                          @click="selectSortOption(option.value)"
                        >
                          <div class="form-check">
                            <input
                              type="radio"
                              class="form-check-input"
                              :checked="sortBy === option.value"
                              readonly
                            />
                            <span class="form-check-label">{{
                              option.label
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filter -->
          <div class="d-none d-lg-flex gap-2">
            <FilterDropdown
              v-for="filter in availableFilters"
              :key="filter.key"
              :filter="filter"
              :is-open="isDropdownOpen(filter.key)"
              :selected-values="getSelectedValues(filter.key)"
              :options="filterOptions[filter.optionsKey]"
              @toggle="toggleDropdown"
              @select="handleFilterSelect"
              @select-all="handleSelectAll"
            />
          </div>

          <!-- Sort dropdown -->
          <div class="filter-select d-none d-lg-block ms-auto">
            <button
              class="form-select"
              :class="{ active: isDropdownOpen('sort') }"
              type="button"
              @click="toggleDropdown('sort')"
            >
              Sorteren
            </button>
            <div v-if="isDropdownOpen('sort')" class="filter-select__dropdown">
              <div
                v-for="option in sortOptions"
                :key="option.value"
                class="item"
                @click="selectSortOption(option.value)"
              >
                <div class="form-check">
                  <input
                    type="radio"
                    class="form-check-input"
                    :checked="sortBy === option.value"
                    readonly
                  />
                  <span class="form-check-label">{{ option.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Reset button -->
          <div class="col-auto d-none d-lg-block">
            <button
              @click="resetFilters"
              :class="[
                'btn btn--transparent fs-xs',
                { invisible: !hasActiveFilters },
                { disabled: !hasActiveFilters },
              ]"
              :disabled="!hasActiveFilters"
            >
              <i
                class="icon icon-refresh me-1"
                :class="hasActiveFilters ? 'text-primary' : 'text-gray-400'"
              ></i>
              Reset
            </button>
          </div>
        </div>
      </div>

      <!-- Active filters -->
      <div class="row mt-3" v-if="hasActiveFilters">
        <div class="col-12">
          <div class="d-flex flex-wrap gap-1">
            <!-- Reset button mobile -->
            <button
              @click="resetFilters"
              :class="['btn btn--transparent fs-xs me-1 d-lg-none']"
            >
              <i
                class="icon icon-refresh me-1"
                :class="hasActiveFilters ? 'text-primary' : 'text-gray-400'"
              ></i>
              Reset
            </button>

            <TooltipTag
              v-for="tag in activeFilterTags"
              :key="tag.key"
              :name="tag.name"
              @remove="tag.remove"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip } from 'bootstrap';
import TooltipTag from './TooltipTag.vue';
import FilterDropdown from './FilterDropdown.vue';
import { debounce, simpleDebounce } from '../utils/performanceUtils.js';
import {
  deviceUtils,
  htmlUtils,
  validationUtils,
} from '../utils/componentUtils.js';

window.globalFilterCache = null;

export default {
  name: 'ProductFilters',
  components: {
    TooltipTag,
    FilterDropdown,
  },
  emits: ['filters-changed', 'mailbox-filter-active'],
  props: {
    allProducts: {
      type: Array,
      default: () => [],
    },
    endpoint: {
      type: String,
      required: true,
    },
    searchPlaceholder: {
      type: String,
      default: "Zoek bijv. op 'goud' (min. 3 karakters of Enter)",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterOptions: {
        categories: [],
        colors: [],
        priceRanges: [],
        attributes: [],
      },
      selectedFilters: {
        categories: [],
        colors: [],
        priceRange: null,
        attributes: [],
      },
      sortBy: 'default',
      sortOptions: [
        { value: 'default', label: 'Standaard' },
        { value: 'name-asc', label: 'A-Z' },
        { value: 'name-desc', label: 'Z-A' },
        { value: 'price-asc', label: 'Prijs laag-hoog' },
        { value: 'price-desc', label: 'Prijs hoog-laag' },
      ],
      localSearch: '',
      totalItems: null,
      debounceTimeout: null,
      _filterChangeTimeout: null,
      minSearchLength: 3,
      openDropdowns: [],
      filtersLoaded: false,
      filterModalOpen: false,
      categoryNamesCache: {},
      currentCategoryLevel: 'main',
      parentCategory: null,
      subcategoriesCache: {},
      loadingSubcategories: false,
      _hasActiveFiltersCache: null,
      _activeFiltersCountCache: null,
      _availableFiltersCache: null,
    };
  },
  computed: {
    hasActiveFilters() {
      const cacheKey = `${this.selectedFilters.categories.length}-${this.selectedFilters.colors.length}-${this.selectedFilters.priceRange ? '1' : '0'}-${this.selectedFilters.attributes.length}`;

      if (
        !this._hasActiveFiltersCache ||
        this._hasActiveFiltersCache.key !== cacheKey
      ) {
        this._hasActiveFiltersCache = {
          key: cacheKey,
          value:
            this.selectedFilters.categories.length > 0 ||
            this.selectedFilters.colors.length > 0 ||
            this.selectedFilters.priceRange !== null ||
            this.selectedFilters.attributes.length > 0,
        };
      }

      return this._hasActiveFiltersCache.value;
    },
    getActiveFiltersCount() {
      const cacheKey = `${this.selectedFilters.categories.length}-${this.selectedFilters.colors.length}-${this.selectedFilters.priceRange ? '1' : '0'}-${this.selectedFilters.attributes.length}`;

      if (
        !this._activeFiltersCountCache ||
        this._activeFiltersCountCache.key !== cacheKey
      ) {
        this._activeFiltersCountCache = {
          key: cacheKey,
          value:
            this.selectedFilters.categories.length +
            this.selectedFilters.colors.length +
            (this.selectedFilters.priceRange !== null ? 1 : 0) +
            this.selectedFilters.attributes.length,
        };
      }

      return this._activeFiltersCountCache.value;
    },
    availableFilters() {
      const cacheKey = `${this.filterOptions.categories?.length || 0}-${this.filterOptions.colors?.length || 0}-${this.filterOptions.priceRanges?.length || 0}-${this.filterOptions.attributes?.length || 0}`;

      if (
        !this._availableFiltersCache ||
        this._availableFiltersCache.key !== cacheKey
      ) {
        const filters = [
          {
            key: 'categories',
            label: 'Categorie',
            optionsKey: 'categories',
            type: 'hierarchical-multi',
            allLabel: 'Alle categorieën',
          },
          {
            key: 'colors',
            label: 'Kleur',
            optionsKey: 'colors',
            type: 'multi',
            allLabel: 'Alle kleuren',
            sortOptions: true,
          },
          {
            key: 'price',
            label: 'Prijs',
            optionsKey: 'priceRanges',
            type: 'single',
            allLabel: 'Alle prijzen',
          },
          {
            key: 'attributes',
            label: 'Kenmerken',
            optionsKey: 'attributes',
            type: 'multi',
            allLabel: 'Alle kenmerken',
          },
        ];

        this._availableFiltersCache = {
          key: cacheKey,
          value: filters.filter((filter) => {
            if (filter.key === 'categories') {
              return true;
            }
            return this.filterOptions[filter.optionsKey]?.length > 0;
          }),
        };
      }

      return this._availableFiltersCache.value;
    },
    activeFilterTags() {
      const categoriesKey = this.selectedFilters.categories.join(',');
      const colorsKey = this.selectedFilters.colors.join(',');
      const attributesKey = this.selectedFilters.attributes.join(',');
      const priceKey = this.selectedFilters.priceRange
        ? this.selectedFilters.priceRange.label
        : 'null';
      const attributesOptionsKey = this.filterOptions.attributes?.length || 0;
      const cacheKey = `${categoriesKey}-${colorsKey}-${priceKey}-${attributesKey}-${attributesOptionsKey}`;

      if (
        !this._activeFilterTagsCache ||
        this._activeFilterTagsCache.key !== cacheKey
      ) {
        const tags = [];

        this.selectedFilters.categories.forEach((categoryId) => {
          if (categoryId === '__back__' || categoryId === 0) {
            return;
          }

          let category = this.filterOptions.categories.find(
            (cat) => cat.id === categoryId || cat.term_id === categoryId,
          );

          const categoryName =
            category?.name || this.categoryNamesCache[categoryId];

          let hasSelectedChild = false;
          if (category && category.children && category.children.length) {
            const childIds = category.children.map(
              (child) => child.id || child.term_id,
            );
            hasSelectedChild = childIds.some((id) =>
              this.selectedFilters.categories.includes(id),
            );
          }

          if (categoryName && !hasSelectedChild) {
            tags.push({
              key: `category-${categoryId}`,
              name: htmlUtils.decodeHtml(categoryName),
              remove: () => this.removeFilter('categories', categoryId),
            });
          }
        });

        this.selectedFilters.colors.forEach((colorId) => {
          const colorObj = this.filterOptions.colors.find(
            (c) => c.term_id === colorId,
          );
          tags.push({
            key: `color-${colorId}`,
            name: colorObj ? colorObj.name : colorId,
            remove: () => this.removeFilter('colors', colorId),
          });
        });

        this.selectedFilters.attributes.forEach((termId) => {
          const attrObj =
            this.filterOptions.attributes?.find((a) => a.term_id === termId) ||
            window.globalFilterCache?.attributes?.find(
              (a) => a.term_id === termId,
            );
          const attributeName = attrObj?.name || attrObj?.label || termId;
          tags.push({
            key: `attribute-${termId}`,
            name: attributeName,
            remove: () => this.removeFilter('attributes', termId),
          });
        });

        if (this.selectedFilters.priceRange) {
          tags.push({
            key: 'price',
            name: this.selectedFilters.priceRange.label,
            remove: () => this.removeFilter('price'),
          });
        }

        this._activeFilterTagsCache = {
          key: cacheKey,
          value: tags,
        };
      }

      return this._activeFilterTagsCache.value;
    },
  },
  mounted() {
    if (window.globalProductCache[this.endpoint]) {
      this.loadFilterOptions();
    }

    this.initializeMailboxFilterState();
    this.addClickOutsideListener();
    this.initializeTooltips();
    this.addReloadListener();

    this.$nextTick(() => {
      if (this.selectedFilters.categories.length > 0) {
        this.updateColorsFilter();
      }
    });
  },
  beforeUnmount() {
    this.removeClickOutsideListener();
    this.removeReloadListener();
    this.clearMemoizationCache();
  },
  methods: {
    clearMemoizationCache() {
      this._hasActiveFiltersCache = null;
      this._activeFiltersCountCache = null;
      this._availableFiltersCache = null;
      this._activeFilterTagsCache = null;

      if (this._debouncedFunctions) {
        this._debouncedFunctions.forEach((debouncedFn) => {
          if (debouncedFn.cancel) {
            debouncedFn.cancel();
          }
        });
        this._debouncedFunctions.clear();
      }
    },

    async loadFilterOptionsFromAPI() {
      await fetch('/api/filters')
        .then((res) => res.json())
        .then((data) => {
          window.globalFilterCache = data;
        })
        .finally(() => {
          this.filtersLoaded = true;
        });
    },

    getFilteredProducts(excludeFilter = null) {
      const products = window.globalProductCache[this.endpoint] || [];
      const isMailboxSession =
        sessionStorage.getItem('s7_is_mailbox_session') === 'true';

      return products.filter((product) => {
        if (isMailboxSession && !product.brievenbus) {
          return false;
        }

        if (
          excludeFilter !== 'categories' &&
          this.selectedFilters.categories.length > 0
        ) {
          const productCategoryIds = product.category_ids || [];
          if (
            !this.selectedFilters.categories.some((id) =>
              productCategoryIds.includes(id),
            )
          ) {
            return false;
          }
        }

        if (
          excludeFilter !== 'colors' &&
          this.selectedFilters.colors.length > 0
        ) {
          const productColorIds = product.color_term_ids || [];
          if (
            !this.selectedFilters.colors.some((id) =>
              productColorIds.includes(id),
            )
          ) {
            return false;
          }
        }

        if (
          excludeFilter !== 'attributes' &&
          this.selectedFilters.attributes.length > 0
        ) {
          const productAttributeIds = product.attribute_term_ids || [];
          if (
            !this.selectedFilters.attributes.every((id) =>
              productAttributeIds.includes(id),
            )
          ) {
            return false;
          }
        }

        if (excludeFilter !== 'price' && this.selectedFilters.priceRange) {
          const { min, max } = this.selectedFilters.priceRange;
          const price = parseFloat(product.price);
          if (price < min || (max !== null && price >= max)) {
            return false;
          }
        }

        return true;
      });
    },

    formatPrice(price) {
      return price === Math.floor(price)
        ? `€${price}`
        : `€${price.toFixed(2).replace('.', ',')}`;
    },

    generateSmartPriceRanges(products) {
      if (!products || products.length === 0) {
        return [];
      }

      const prices = products
        .map((p) => parseFloat(p.price))
        .filter((p) => !isNaN(p) && p > 0)
        .sort((a, b) => a - b);

      if (prices.length === 0) {
        return [];
      }

      const minPrice = Math.floor(Math.min(...prices) * 2) / 2;
      const maxPrice = Math.ceil(Math.max(...prices));

      const priceSteps = [
        { min: 0, max: 10, step: 2.5 },
        { min: 10, max: 25, step: 5 },
        { min: 25, max: 50, step: 10 },
        { min: 50, max: 100, step: 25 },
        { min: 100, max: null, step: null },
      ];

      const ranges = [];
      const addedRanges = new Set();

      for (const stepConfig of priceSteps) {
        if (stepConfig.min > maxPrice) break;

        if (stepConfig.max === null) {
          const count = products.filter(
            (p) => p.price >= stepConfig.min,
          ).length;
          if (count > 0) {
            ranges.push({
              min: stepConfig.min,
              max: null,
              label: `${this.formatPrice(stepConfig.min)}+`,
            });
          }
          break;
        }

        let current = Math.max(stepConfig.min, minPrice);
        while (current < stepConfig.max && current < maxPrice) {
          const nextValue = Math.min(current + stepConfig.step, stepConfig.max);
          const rangeKey = `${current}-${nextValue}`;
          const count = products.filter(
            (p) => p.price >= current && p.price < nextValue,
          ).length;

          if (count > 0 && !addedRanges.has(rangeKey)) {
            ranges.push({
              min: current,
              max: nextValue,
              label: `${this.formatPrice(current)} - ${this.formatPrice(nextValue)}`,
            });
            addedRanges.add(rangeKey);
          }

          current = nextValue;
        }
      }

      return ranges;
    },

    async loadFilterOptions() {
      if (!window.globalFilterCache) {
        await this.loadFilterOptionsFromAPI();
      }

      const categoryIdsInProducts = new Set();
      const colorIdsInProducts = new Set();
      const attributeIdsInProducts = new Set();
      const categoryCounts = {};

      window.globalProductCache[this.endpoint].forEach((product) => {
        if (product.category_ids && Array.isArray(product.category_ids)) {
          product.category_ids.forEach((id) => {
            categoryIdsInProducts.add(id);
            categoryCounts[id] = (categoryCounts[id] || 0) + 1;
          });
        }
        product.color_term_ids?.forEach((id) => colorIdsInProducts.add(id));
        product.attribute_term_ids?.forEach((id) =>
          attributeIdsInProducts.add(id),
        );
      });

      this.filterOptions = JSON.parse(JSON.stringify(window.globalFilterCache));
      this.filterOptions.categories =
        this.filterOptions.categories?.filter(
          (category) => category.kind_of_category === this.endpoint,
        ) || [];

      this.filterOptions.categories = this.filterOptions.categories
        .filter((category) =>
          categoryIdsInProducts.has(category.id || category.term_id),
        )
        .map((category) => ({
          ...category,
          count: categoryCounts[category.id || category.term_id] || 0,
        }));

      this.filterOptions.colors = (this.filterOptions.colors || []).filter(
        (color) => colorIdsInProducts.has(color.id || color.term_id),
      );

      this.filterOptions.attributes = (
        this.filterOptions.attributes || []
      ).filter((attr) => attributeIdsInProducts.has(attr.term_id));

      this.filterOptions.priceRanges = this.generateSmartPriceRanges(
        window.globalProductCache[this.endpoint],
      );

      if (this.filterOptions.categories.length <= 1) {
        this.filterOptions.categories = [];
      }

      if (this.filterOptions.colors.length <= 1) {
        this.filterOptions.colors = [];
      }

      if (this.filterOptions.priceRanges.length <= 1) {
        this.filterOptions.priceRanges = [];
      }

      this.updateCategoryCounts();
      this.updatePriceRanges();

      this.filtersLoaded = true;
    },

    handleSearchInput() {
      if (this.$parent && typeof this.$parent.blurLoader !== 'undefined') {
        this.$parent.blurLoader = true;
      }

      this.$nextTick(() => {
        if (this.$refs.searchInput && !this.isMobileDevice()) {
          this.$refs.searchInput.focus();
        }
      });

      const searchTerm = this.localSearch.trim();
      if (
        searchTerm.length === 0 ||
        searchTerm.length >= this.minSearchLength
      ) {
        this.debounce(async () => await this.debouncedApplyFilters(), 500);
      }
    },

    handleSearchEnter() {
      if (this.$parent && typeof this.$parent.blurLoader !== 'undefined') {
        this.$parent.blurLoader = true;
      }

      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      this.debouncedApplyFilters();
    },

    debounce(func, delay) {
      if (!this._debouncedFunctions) {
        this._debouncedFunctions = new Map();
      }

      const key = `${func.name || 'anonymous'}_${delay}`;

      if (!this._debouncedFunctions.has(key)) {
        this._debouncedFunctions.set(
          key,
          debounce(func, delay, {
            leading: false,
            trailing: true,
            maxWait: delay * 2,
          }),
        );
      }

      return this._debouncedFunctions.get(key)();
    },

    debouncedApplyFilters() {
      clearTimeout(this._filterChangeTimeout);
      this._filterChangeTimeout = setTimeout(async () => {
        await this.updateColorsFilter();
        this.updateCategoryCounts();
        this.updatePriceRanges();
        await this.applyFilters();
      }, 50);
    },

    restoreFocus() {
      this.$nextTick(() => {
        if (
          this.$refs.searchInput &&
          document.activeElement !== this.$refs.searchInput &&
          !this.isMobileDevice()
        ) {
          this.$refs.searchInput.focus();
        }
      });
    },

    toggleDropdown(dropdownName) {
      const index = this.openDropdowns.indexOf(dropdownName);
      if (index > -1) {
        this.openDropdowns.splice(index, 1);
      } else {
        this.openDropdowns = [dropdownName];
      }
    },

    isDropdownOpen(dropdownName) {
      return this.openDropdowns.includes(dropdownName);
    },

    isMobileDevice() {
      return deviceUtils.isMobile();
    },

    getSelectedValues(filterKey) {
      switch (filterKey) {
        case 'categories':
          return this.selectedFilters.categories;
        case 'colors':
          return this.selectedFilters.colors;
        case 'attributes':
          return this.selectedFilters.attributes;
        case 'price':
          return this.selectedFilters.priceRange
            ? [this.selectedFilters.priceRange]
            : [];
        default:
          return [];
      }
    },

    async handleFilterSelect(filterKey, option) {
      if (this.$parent && typeof this.$parent.blurLoader !== 'undefined') {
        this.$parent.blurLoader = true;
      }

      switch (filterKey) {
        case 'categories':
          const categoryId = option.id || option.term_id;
          const wasSelected =
            this.selectedFilters.categories.includes(categoryId);
          this.toggleArrayValue(this.selectedFilters.categories, categoryId);
          if (wasSelected && option.children && option.children.length) {
            option.children.forEach((child) => {
              const childId = child.id || child.term_id;
              const idx = this.selectedFilters.categories.indexOf(childId);
              if (idx !== -1) {
                this.selectedFilters.categories.splice(idx, 1);
              }
            });
          }
          if (option.name) {
            this.categoryNamesCache[categoryId] = option.name;
          }
          await this.debouncedApplyFilters();
          break;
        case 'colors':
          this.toggleArrayValue(this.selectedFilters.colors, option.term_id);
          await this.debouncedApplyFilters();
          break;
        case 'attributes':
          this.toggleArrayValue(
            this.selectedFilters.attributes,
            option.term_id,
          );
          await this.debouncedApplyFilters();
          break;
        case 'price':
          this.selectedFilters.priceRange =
            this.selectedFilters.priceRange === option ? null : option;
          this.openDropdowns = [];
          await this.debouncedApplyFilters();
          break;
      }
      this.$nextTick(() => {
        if (this.$refs.searchInput && !this.isMobileDevice()) {
          this.$refs.searchInput.focus();
        }
      });
    },

    async handleSelectAll(filterKey) {
      switch (filterKey) {
        case 'categories':
          this.selectedFilters.categories = [];
          break;
        case 'colors':
          this.selectedFilters.colors = [];
          break;
        case 'attributes':
          this.selectedFilters.attributes = [];
          break;
        case 'price':
          this.selectedFilters.priceRange = null;
          break;
      }
      this.openDropdowns = [];
      await this.debouncedApplyFilters();
      this.$nextTick(() => {
        if (this.$refs.searchInput && !this.isMobileDevice()) {
          this.$refs.searchInput.focus();
        }
      });
    },

    toggleArrayValue(array, value) {
      const index = array.indexOf(value);
      if (index > -1) {
        array.splice(index, 1);
      } else {
        array.push(value);
      }
    },

    async removeFilter(filterKey, value = null) {
      switch (filterKey) {
        case 'categories':
          this.toggleArrayValue(this.selectedFilters.categories, value);
          const parent = (this.filterOptions.categories || []).find(
            (cat) => (cat.id || cat.term_id) === value,
          );
          if (parent && parent.children && parent.children.length) {
            parent.children.forEach((child) => {
              const childId = child.id || child.term_id;
              const idx = this.selectedFilters.categories.indexOf(childId);
              if (idx !== -1) {
                this.selectedFilters.categories.splice(idx, 1);
              }
            });
          }
          break;
        case 'colors':
          this.toggleArrayValue(this.selectedFilters.colors, value);
          break;
        case 'attributes':
          this.toggleArrayValue(this.selectedFilters.attributes, value);
          break;
        case 'price':
          this.selectedFilters.priceRange = null;
          break;
      }
      await this.debouncedApplyFilters();
      this.$nextTick(() => {
        if (this.$refs.searchInput && !this.isMobileDevice()) {
          this.$refs.searchInput.focus();
        }
      });
    },

    async selectSortOption(value) {
      this.sortBy = value;
      this.openDropdowns = [];
      if (this.$parent && typeof this.$parent.blurLoader !== 'undefined') {
        this.$parent.blurLoader = true;
      }
      await this.debouncedApplyFilters();
    },

    addClickOutsideListener() {
      document.addEventListener('click', this.handleClickOutside);
    },

    removeClickOutsideListener() {
      document.removeEventListener('click', this.handleClickOutside);
    },

    addReloadListener() {
      document.addEventListener(
        's7_reload_products',
        this.handleProductsReload,
      );
    },

    removeReloadListener() {
      document.removeEventListener(
        's7_reload_products',
        this.handleProductsReload,
      );
    },

    handleProductsReload() {
      this.$nextTick(() => {
        this.updateCategoryCounts();
        this.updatePriceRanges();
      });
    },

    handleClickOutside(event) {
      const isDropdownItem = event.target.closest('.item');
      if (isDropdownItem) return;

      if (!this.$el.contains(event.target)) {
        this.openDropdowns = [];
      }
    },

    async resetFilters() {
      this.selectedFilters = {
        categories: [],
        colors: [],
        attributes: [],
        priceRange: null,
      };
      this.localSearch = '';
      this.currentCategoryLevel = 'main';
      this.parentCategory = null;
      this.subcategoriesCache = {};

      this.clearMemoizationCache();

      await this.debouncedApplyFilters();
      this.$nextTick(() => {
        if (this.$refs.searchInput && !this.isMobileDevice()) {
          this.$refs.searchInput.focus();
        }
      });
    },

    async resetFiltersOnly() {
      this.selectedFilters = {
        categories: [],
        colors: [],
        attributes: [],
        priceRange: null,
      };
      this.currentCategoryLevel = 'main';
      this.parentCategory = null;
      this.subcategoriesCache = {};

      this.clearMemoizationCache();

      await this.debouncedApplyFilters();
      this.$nextTick(() => {
        if (this.$refs.searchInput && !this.isMobileDevice()) {
          this.$refs.searchInput.focus();
        }
      });
    },

    async applyFilters() {
      if (this.$parent && typeof this.$parent.blurLoader !== 'undefined') {
        this.$parent.blurLoader = true;
      }

      this.debounce(async () => {
        const params = new URLSearchParams();

        if (this.localSearch.trim()) {
          params.set('s', this.localSearch.trim());
        }

        let selectedCategoryIds = [...this.selectedFilters.categories];
        const parentIdsWithSelectedChildren = new Set();
        (this.filterOptions.categories || []).forEach((parent) => {
          if (parent.children && parent.children.length) {
            const selectedChildIds = parent.children
              .map((child) => child.id || child.term_id)
              .filter((childId) =>
                this.selectedFilters.categories.includes(childId),
              );
            if (selectedChildIds.length > 0) {
              parentIdsWithSelectedChildren.add(parent.id || parent.term_id);
            }
          }
        });

        selectedCategoryIds = selectedCategoryIds.filter(
          (id) => !parentIdsWithSelectedChildren.has(id),
        );

        selectedCategoryIds.forEach((categoryId) => {
          params.append('categories', categoryId);
        });

        this.selectedFilters.colors.forEach((color) => {
          params.append('colors', color);
        });

        this.selectedFilters.attributes.forEach((attribute) => {
          params.append('attributes', attribute);
        });

        if (this.selectedFilters.priceRange) {
          params.set('minPrice', this.selectedFilters.priceRange.min);
          if (this.selectedFilters.priceRange.max !== null) {
            params.set('maxPrice', this.selectedFilters.priceRange.max);
          }
        }

        this.$emit('filters-changed', {
          search: this.localSearch,
          filters: {
            categories: selectedCategoryIds,
            colors: this.selectedFilters.colors,
            attributes: this.selectedFilters.attributes,
            priceRange: this.selectedFilters.priceRange,
          },
          sortBy: this.sortBy,
          params: params,
        });

        this.reinitializeTooltips();

        this.restoreFocus();
      }, 100);
    },

    updateCategoryCounts() {
      const filteredProducts = this.getFilteredProducts('categories');
      const categoryCounts = {};

      filteredProducts.forEach((product) => {
        if (product.category_ids && Array.isArray(product.category_ids)) {
          product.category_ids.forEach((id) => {
            categoryCounts[id] = (categoryCounts[id] || 0) + 1;
          });
        }
      });

      this.selectedFilters.categories = this.selectedFilters.categories.filter(
        (id) => categoryCounts[id] > 0,
      );

      this.filterOptions.categories = this.filterOptions.categories.map(
        (category) => {
          const updatedCategory = {
            ...category,
            count: categoryCounts[category.id || category.term_id] || 0,
          };

          if (category.children && Array.isArray(category.children)) {
            updatedCategory.children = category.children.map((child) => ({
              ...child,
              count: categoryCounts[child.id || child.term_id] || 0,
            }));
          }

          return updatedCategory;
        },
      );
    },

    async updateColorsFilter() {
      const filteredProducts = this.getFilteredProducts('colors');
      const colorCounts = {};

      filteredProducts.forEach((product) => {
        if (product.color_term_ids && Array.isArray(product.color_term_ids)) {
          product.color_term_ids.forEach((id) => {
            colorCounts[id] = (colorCounts[id] || 0) + 1;
          });
        }
      });

      const allColors = window.globalFilterCache?.colors || [];
      this.filterOptions.colors = allColors
        .map((color) => ({
          ...color,
          count: colorCounts[color.term_id] || 0,
        }))
        .filter((color) => color.count > 0);

      this.selectedFilters.colors = this.selectedFilters.colors.filter(
        (id) => colorCounts[id] > 0,
      );
    },

    updatePriceRanges() {
      const filteredProducts = this.getFilteredProducts('price');
      const smartRanges = this.generateSmartPriceRanges(filteredProducts);

      this.filterOptions.priceRanges = smartRanges
        .map((priceRange) => {
          const count = filteredProducts.filter((product) => {
            const price = parseFloat(product.price);
            return (
              price >= priceRange.min &&
              (priceRange.max === null || price < priceRange.max)
            );
          }).length;

          return { ...priceRange, count };
        })
        .filter((priceRange) => priceRange.count > 0);

      if (
        this.selectedFilters.priceRange &&
        !this.filterOptions.priceRanges.some(
          (range) =>
            range.min === this.selectedFilters.priceRange.min &&
            range.max === this.selectedFilters.priceRange.max,
        )
      ) {
        this.selectedFilters.priceRange = null;
      }
    },

    updateTotalItems(total) {
      this.totalItems = total;
    },

    focusSearchInput() {
      this.$nextTick(() => {
        if (this.$refs.searchInput && !this.isMobileDevice()) {
          this.$refs.searchInput.focus();
        }
      });
    },

    clearSearchInput() {
      this.$refs.searchInput && (this.$refs.searchInput.value = '');
      this.localSearch = '';
    },

    initializeTooltips() {
      document
        .querySelectorAll('[data-bs-toggle="tooltip"]')
        .forEach((el) => new Tooltip(el));
    },

    reinitializeTooltips() {
      this.$nextTick(this.initializeTooltips);
    },

    initializeMailboxFilterState() {
      const isMailboxSession =
        sessionStorage.getItem('s7_is_mailbox_session') === 'true';

      if (isMailboxSession) {
        this.$nextTick(() => {
          this.$emit('mailbox-filter-active', true);
        });
      }
    },
  },
};
</script>
